import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { createClient, getUserApiKey } from '@/lib/supabase-client'
import ImageUploader from './ImageUploader'
import PublicHeader from './PublicHeader'
import BlogGeneratorForm from './BlogGeneratorForm'
import RotatingToolText from './RotatingToolText'
import RotatingUserText from './RotatingUserText'
import GoogleOneTap from './GoogleOneTap'

function SimpleTTSForm() {
  const [text, setText] = useState('')
  const [language, setLanguage] = useState('en')
  const [speed, setSpeed] = useState(1.0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const supabase = createClient()
  // TTS API endpoints are ONLY on Cloud Run: api.gistmag.co.uk/tts (not on Supabase)
  const apiBaseUrl = 'https://api.gistmag.co.uk'

  const generateTTS = async () => {
    if (!text.trim()) {
      setError('Please enter some text to convert to speech')
      return
    }

    setLoading(true)
    setError(null)
    setAudioUrl(null)

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        setError('Please sign in to use TTS. Sign up for a free account!')
        setLoading(false)
        return
      }

      const apiKey = await getUserApiKey(supabase, session.user.id)

      if (!apiKey) {
        setError('Unable to retrieve API key. Please try refreshing the page.')
        setLoading(false)
        return
      }

      const response = await fetch(`${apiBaseUrl}/tts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          api_key: apiKey,
          text: text,
          language: language,
          speed: speed,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        setError(errorData.error || errorData.message || 'Failed to generate speech')
        setLoading(false)
        return
      }

      const audioBlob = await response.blob()
      const url = URL.createObjectURL(audioBlob)
      setAudioUrl(url)
    } catch (err: any) {
      setError(err.message || 'An error occurred while generating speech')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Text-to-Speech Generator</h3>
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Enter Text
        </label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type or paste the text you want to convert to speech..."
          className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
          rows={4}
        />
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          {text.length} characters • Credits: {Math.max(1, Math.ceil(text.length / 1000))} per 1000 characters
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Language
          </label>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
          >
            <option value="en">English (US)</option>
            <option value="es">Spanish</option>
            <option value="fr">French</option>
            <option value="de">German</option>
            <option value="it">Italian</option>
            <option value="pt">Portuguese</option>
            <option value="ja">Japanese</option>
            <option value="ko">Korean</option>
            <option value="zh">Chinese</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Speed: {speed.toFixed(1)}x
          </label>
          <input
            type="range"
            min="0.25"
            max="4.0"
            step="0.1"
            value={speed}
            onChange={(e) => setSpeed(parseFloat(e.target.value))}
            className="w-full"
          />
        </div>
      </div>

      <button
        onClick={generateTTS}
        disabled={loading || !text.trim()}
        className="w-full px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition"
      >
        {loading ? 'Generating Speech...' : 'Generate Speech'}
      </button>

      {error && (
        <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {audioUrl && (
        <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <p className="text-sm font-medium text-green-700 dark:text-green-400 mb-3">
            ✅ Speech generated successfully!
          </p>
          <audio controls className="w-full" src={audioUrl}>
            Your browser does not support the audio element.
          </audio>
          <div className="mt-3">
            <a
              href={audioUrl}
              download="speech.wav"
              className="inline-block px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition"
            >
              Download Audio
            </a>
          </div>
        </div>
      )}
    </div>
  )
}

export default function LandingPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [activeToolTab, setActiveToolTab] = useState<'alt-text' | 'tts' | 'blog-generator' | 'site-audit' | 'indexing'>('alt-text')
  const [loading, setLoading] = useState(false)
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null)
  const [carouselIndex, setCarouselIndex] = useState(0)
  const [dbPlans, setDbPlans] = useState<Array<{ name: string; credits: string; price: string; period: string; features: string[]; popular?: boolean }>>([])
  const supabase = createClient()

  useEffect(() => {
    checkAuth()
    loadPricingPlans()
    // Handle hash fragments for pricing and FAQ sections
    const hash = window.location.hash
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash.substring(1))
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' })
        }
      }, 100)
    }
  }, [])

  // Auto-rotate carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCarouselIndex((prev) => (prev === 4 ? 0 : prev + 1))
    }, 5000) // Change slide every 5 seconds

    return () => clearInterval(interval)
  }, [])

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession()
    setIsAuthenticated(!!session)
  }

  const loadPricingPlans = async () => {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('name, credits_per_period, price_monthly_cents, sort_order')
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
      if (error || !data) return

      const featuresByTier: Record<string, string[]> = {
        Free: ['One-time credits', 'All modes', 'API access', 'All extensions & plugins', 'Community support'],
        Starter: ['Credits/month', 'All modes', 'Priority processing', 'All extensions & plugins', 'Email support'],
        Pro: ['Credits/month', 'All modes', 'Priority processing', 'All extensions & plugins', 'Priority support'],
        Business: ['Credits/month', 'All modes', 'Priority processing', 'All extensions & plugins', 'Priority support'],
        Enterprise: ['Credits/month', 'All modes', 'Highest priority', 'All extensions & plugins', 'Dedicated support'],
      }
      const popularPlans = ['Pro']

      const mapped = data.map((p: any) => {
        const credits = p.credits_per_period.toLocaleString()
        const price = p.price_monthly_cents === 0 ? '$0' : `$${(p.price_monthly_cents / 100).toFixed(0)}`
        const isFree = p.price_monthly_cents === 0
        const features = featuresByTier[p.name] || featuresByTier['Starter']
        // Replace first feature with actual credits
        const updatedFeatures = [
          isFree ? `${credits} credits (one-time)` : `${credits} credits/month`,
          ...features.slice(1),
        ]
        return {
          name: p.name,
          credits,
          price,
          period: 'month',
          features: updatedFeatures,
          popular: popularPlans.includes(p.name),
        }
      })
      if (mapped.length > 0) setDbPlans(mapped)
    } catch {
      // Fall back to hardcoded plans
    }
  }

  const handleGoogleLogin = async () => {
    try {
      setLoading(true)
      const redirectTo = `${window.location.origin}/dashboard`
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
        },
      })
      if (error) {
        console.error('Google login error:', error)
        setLoading(false)
      }
    } catch (err: any) {
      console.error('Google login failed:', err)
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 text-gray-900 dark:text-white transition-colors">
      {!isAuthenticated && <GoogleOneTap />}
      <PublicHeader hideMakeAndDashboard={true} />

      {/* Hero Section */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Badge */}
        <div className="flex items-center justify-center mb-6 animate-fade-in">
          <span className="inline-flex items-center space-x-2 px-4 py-1.5 bg-primary-500/20 border border-primary-500/50 rounded-full text-primary-400 text-sm font-medium">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            <span>Gist Magazines • Gist Images • Gist Speech • Gist Blog • 100% Free</span>
          </span>
        </div>

        {/* Main Title */}
        <div className="text-center mb-8 animate-fade-in" style={{ animationDelay: '0.1s' }}>
          <h1 className="text-6xl md:text-7xl font-bold mb-4">
            <span className="text-gray-900 dark:text-white">AI-Powered</span>
            <br />
            <RotatingToolText />
          </h1>
          <RotatingUserText />
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          {isAuthenticated ? (
            <Link
              to="/dashboard"
              className="px-8 py-4 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition hover-lift flex items-center space-x-2"
            >
              <span>Go to Dashboard →</span>
            </Link>
          ) : (
            <>
              <button
                onClick={handleGoogleLogin}
                disabled={loading}
                className="px-8 py-4 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-400 text-white font-semibold rounded-lg transition hover-lift flex items-center space-x-2 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span>Signing in...</span>
                ) : (
                  <>
                    <img
                      src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                      alt=""
                      className="w-5 h-5"
                    />
                    <span>Login with Google</span>
                  </>
                )}
              </button>
              <Link
                to="/auth/login"
                className="px-8 py-4 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-900 dark:text-white font-semibold rounded-lg transition hover-lift flex items-center space-x-2 border border-gray-300 dark:border-slate-700"
              >
                <span>Sign In</span>
              </Link>
            </>
          )}
          <button
            onClick={() => {
              const imageUploader = document.getElementById('image-uploader')
              imageUploader?.scrollIntoView({ behavior: 'smooth' })
            }}
            className="px-8 py-4 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-900 dark:text-white font-semibold rounded-lg transition hover-lift flex items-center space-x-2 border border-gray-300 dark:border-slate-700"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            <span>Try AI Image Generator</span>
          </button>
        </div>

        {/* Trusted By Section */}
        <div className="mb-20 animate-fade-in" style={{ animationDelay: '0.3s' }}>
          <p className="text-center text-sm text-gray-600 dark:text-gray-400 mb-6 font-medium">
            Trusted by businesses across industries
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12 opacity-60 hover:opacity-100 transition-opacity">
            <div className="flex items-center justify-center">
              <img
                src="https://simplifysme.com/favicon.ico"
                alt="SimplifySME"
                className="h-8 w-8 md:h-10 md:w-10 object-contain"
                onError={(e) => {
                  const target = e.target as HTMLImageElement
                  target.style.display = 'none'
                }}
              />
              <span className="ml-2 text-sm md:text-base text-gray-700 dark:text-gray-300 font-medium">SimplifySME</span>
            </div>
            <div className="flex items-center justify-center">
              <img
                src="https://olaverse.co.uk/favicon.ico"
                alt="Olaverse"
                className="h-8 w-8 md:h-10 md:w-10 object-contain"
                onError={(e) => {
                  const target = e.target as HTMLImageElement
                  target.style.display = 'none'
                }}
              />
              <span className="ml-2 text-sm md:text-base text-gray-700 dark:text-gray-300 font-medium">Olaverse</span>
            </div>
            <div className="flex items-center justify-center">
              <img
                src="https://curratedbrief.com/favicon.ico"
                alt="Currated Brief"
                className="h-8 w-8 md:h-10 md:w-10 object-contain"
                onError={(e) => {
                  const target = e.target as HTMLImageElement
                  target.style.display = 'none'
                }}
              />
              <span className="ml-2 text-sm md:text-base text-gray-700 dark:text-gray-300 font-medium">Currated Brief</span>
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div className="grid md:grid-cols-4 gap-6 mb-20">
          {[
            { icon: '📈', title: 'Improve SEO', desc: 'Boost your search rankings with optimized alt text' },
            { icon: '♿', title: 'Accessibility First', desc: 'Make your content accessible to everyone' },
            { icon: '🎙️', title: 'Text-to-Speech', desc: 'Convert text to high-quality speech audio' },
            { icon: '⚡', title: 'Lightning Fast', desc: 'Generate alt text and speech in seconds' },
          ].map((benefit, index) => (
            <div
              key={index}
              className="glass-card rounded-xl p-6 hover-lift animate-scale-in"
              style={{ animationDelay: `${0.3 + index * 0.1}s` }}
            >
              <div className="text-4xl mb-4">{benefit.icon}</div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{benefit.title}</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">{benefit.desc}</p>
            </div>
          ))}
        </div>

        {/* Tools Section with Tabs */}
        <div id="image-uploader" className="mb-20 animate-fade-in">
          <div className="max-w-4xl mx-auto">
            {/* Tabs */}
            <div className="flex justify-center mb-6">
              <div className="inline-flex rounded-lg border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 p-1">
                <button
                  onClick={() => setActiveToolTab('alt-text')}
                  className={`px-6 py-2 rounded-md font-medium transition ${
                    activeToolTab === 'alt-text'
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'
                  }`}
                >
                  🖼️ Alt Text Generator
                </button>
                <button
                  onClick={() => setActiveToolTab('tts')}
                  className={`px-6 py-2 rounded-md font-medium transition ${
                    activeToolTab === 'tts'
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'
                  }`}
                >
                  🎙️ Text-to-Speech
                </button>
                <button
                  onClick={() => setActiveToolTab('blog-generator')}
                  className={`px-6 py-2 rounded-md font-medium transition ${
                    activeToolTab === 'blog-generator'
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'
                  }`}
                >
                  📝 Gist Blog
                </button>
                <button
                  onClick={() => setActiveToolTab('site-audit')}
                  className={`px-6 py-2 rounded-md font-medium transition ${
                    activeToolTab === 'site-audit'
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'
                  }`}
                >
                  🧪 Site Audit
                </button>
                <button
                  onClick={() => setActiveToolTab('indexing')}
                  className={`px-6 py-2 rounded-md font-medium transition ${
                    activeToolTab === 'indexing'
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'
                  }`}
                >
                  🔍 Indexing
                </button>
              </div>
            </div>

            {/* Tab Content */}
            {activeToolTab === 'alt-text' && (
              <div className="glass-card rounded-xl p-6">
                <ImageUploader />
              </div>
            )}

            {activeToolTab === 'tts' && (
              <div className="glass-card rounded-xl p-6">
                <SimpleTTSForm />
              </div>
            )}

            {activeToolTab === 'blog-generator' && (
              <div className="glass-card rounded-xl p-6">
                <BlogGeneratorForm />
              </div>
            )}

            {activeToolTab === 'site-audit' && (
              <div className="glass-card rounded-xl p-6 space-y-4">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Site Audit</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Check how healthy a page is across performance, accessibility, best practices, and SEO with a single
                  audit. You&apos;ll get clear scores plus an optional visual snapshot of the page.
                </p>
                <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  <li>See an overall score from 0–100</li>
                  <li>Breakdowns for performance, accessibility, best practices, and SEO</li>
                  <li>Recent audit history saved to your account</li>
                </ul>
                <div className="pt-2">
                  <Link
                    to={isAuthenticated ? '/dashboard/seo-audit' : '/auth/login'}
                    className="inline-flex items-center px-6 py-3 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold shadow-sm"
                  >
                    <span>{isAuthenticated ? 'Open Site Audit in Dashboard' : 'Sign in to run a Site Audit'}</span>
                    <span className="ml-2">→</span>
                  </Link>
                </div>
              </div>
            )}

            {activeToolTab === 'indexing' && (
              <div className="glass-card rounded-xl p-6 space-y-4">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Indexing Tools</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Check whether your pages are indexed in Google and get them discovered faster.
                  Paste up to 10,000 URLs and we&apos;ll check each one against Google Search results.
                </p>
                <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  <li>Index Check — verify which pages Google has indexed</li>
                  <li>Results show indexed, not indexed, or rate-limited status</li>
                  <li>Export results as CSV for easy reporting</li>
                  <li>Index Submit — instantly notify Bing, Yandex, Seznam &amp; Naver</li>
                </ul>
                <div className="pt-2">
                  <Link
                    to={isAuthenticated ? '/dashboard/indexing' : '/auth/login'}
                    className="inline-flex items-center px-6 py-3 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold shadow-sm"
                  >
                    <span>{isAuthenticated ? 'Open Indexing in Dashboard' : 'Sign in to check your index'}</span>
                    <span className="ml-2">→</span>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Features Section - Carousel */}
        <div className="mb-20">
          <h2 className="text-4xl font-bold text-center mb-12">Why Choose GistMag?</h2>
          <div className="relative max-w-4xl mx-auto">
            <div className="overflow-hidden rounded-xl">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${carouselIndex * 100}%)` }}
              >
                {[
                  { icon: '🎯', title: 'Multiple Modes', desc: 'Short, long, and SEO-optimized alt text options' },
                  { icon: '🎙️', title: 'Text-to-Speech', desc: 'Convert text to high-quality speech audio with customizable voices' },
                  { icon: '🧪', title: 'Site Audit', desc: 'Run PageSpeed-style audits with performance, accessibility, best practices, and SEO scores.' },
                  { icon: '🔍', title: 'Indexing', desc: 'Check whether your pages are indexed in Google. Bulk-check up to 10,000 URLs at once.' },
                  { icon: '📝', title: 'Gist Blog', desc: 'Generate images, excerpt, category, and tags for blog posts' },
                  { icon: '📦', title: 'Batch Processing', desc: 'Process hundreds of images at once. Perfect for large websites.' },
                ].map((feature, index) => (
                  <div
                    key={index}
                    className="min-w-full px-4"
                  >
                    <div className="glass-card rounded-xl p-8 hover-lift">
                      <div className="text-5xl mb-4 text-center">{feature.icon}</div>
                      <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-3 text-center">{feature.title}</h3>
                      <p className="text-gray-600 dark:text-gray-400 text-center">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Carousel Navigation */}
              <div className="flex items-center justify-center gap-4 mt-8">
              <button
                onClick={() => setCarouselIndex((prev) => (prev === 0 ? 4 : prev - 1))}
                className="p-2 rounded-full bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-900 dark:text-white transition"
                aria-label="Previous"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              {/* Dots indicator */}
              <div className="flex gap-2">
                {[0, 1, 2, 3, 4].map((index) => (
                  <button
                    key={index}
                    onClick={() => setCarouselIndex(index)}
                    className={`w-3 h-3 rounded-full transition ${
                      carouselIndex === index
                        ? 'bg-primary-600 dark:bg-primary-500'
                        : 'bg-gray-300 dark:bg-slate-600'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
              
              <button
                onClick={() => setCarouselIndex((prev) => (prev === 4 ? 0 : prev + 1))}
                className="p-2 rounded-full bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-900 dark:text-white transition"
                aria-label="Next"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Supported Integrations Section */}
        <div className="mb-20">
          <h2 className="text-4xl font-bold text-center mb-4">Works With Your Favorite Platforms</h2>
          <p className="text-center text-gray-600 dark:text-gray-400 mb-12">
            Seamlessly integrate GistMag with the tools you already use
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {[
              {
                name: 'Make.com',
                logo: '/make-color.png',
                fallbackIcon: '⚙️',
                color: 'purple',
                link: '/make',
                status: 'available',
                description: 'Automate alt text generation',
                tool: 'Alt Text',
              },
              {
                name: 'WordPress Plugin',
                logo: '/wordpress-logo3.webp',
                fallbackIcon: '📝',
                color: 'blue',
                link: 'https://wordpress.org/plugins/gistmag-alt-text-generator/',
                status: 'available',
                description: 'Auto-generate alt text',
                tool: 'Alt Text',
              },
              {
                name: 'WordPress TTS Reader',
                logo: '/wordpress-logo3.webp',
                fallbackIcon: '🎙️',
                color: 'indigo',
                link: '/docs/integrations/wordpress-tts',
                status: 'available',
                description: 'Text-to-speech for posts',
                tool: 'Text-to-Speech',
              },
              {
                name: 'WordPress Gist Blog',
                logo: '/wordpress-logo3.webp',
                fallbackIcon: '📝',
                color: 'purple',
                link: '/docs/integrations/wordpress-blog-meta-generator',
                status: 'available',
                description: 'Generate images, excerpt, tags',
                tool: 'Gist Blog',
              },
              {
                name: 'Chrome Extension',
                logo: '/Google-Chrome-Logo-for-tech-themed-designs-web-development-graphics-transparent-PNG-image-jpg.png',
                fallbackIcon: '🌐',
                color: 'blue',
                link: 'https://chromewebstore.google.com/detail/gistmag-alt-text-generato/fdlepmdamhcpnlnkpmpkgdhfelogkahi',
                status: 'available',
                description: 'Right-click to generate',
                tool: 'Alt Text',
              },
              {
                name: 'Edge Extension',
                logo: '/png-clipart-edge-browser-logo-tech-companies-thumbnail.png',
                fallbackIcon: '🌐',
                color: 'blue',
                link: 'https://microsoftedge.microsoft.com/addons/detail/gistmag-alt-text-generato/piejclnjcjmlgneiecnnjgmnknlbpbje',
                status: 'available',
                description: 'Right-click to generate',
                tool: 'Alt Text',
              },
              {
                name: 'Firefox Extension',
                logo: '/firefox.png',
                fallbackIcon: '🦊',
                color: 'orange',
                link: 'https://addons.mozilla.org/en-GB/firefox/addon/gistmag-alt-text-generator/',
                status: 'available',
                description: 'Right-click to generate',
                tool: 'Alt Text',
              },
              {
                name: 'Shopify App',
                logo: '/shopify.png',
                fallbackIcon: '🛍️',
                color: 'green',
                link: 'https://apps.shopify.com/gistmag-alt-text-generator',
                status: 'available',
                description: 'Available on Shopify App Store',
                tool: 'Alt Text',
              },
            ].map((integration, index) => (
              <div
                key={integration.name}
                className="glass-card rounded-xl p-6 hover-lift animate-scale-in text-center"
                style={{ animationDelay: `${0.6 + index * 0.1}s` }}
              >
                <div className={`w-16 h-16 mx-auto mb-4 ${
                  integration.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30' :
                  integration.color === 'green' ? 'bg-green-100 dark:bg-green-900/30' :
                  integration.color === 'orange' ? 'bg-orange-100 dark:bg-orange-900/30' :
                  'bg-purple-100 dark:bg-purple-900/30'
                } rounded-xl flex items-center justify-center ${
                  integration.logo ? 'p-2' : 'text-3xl'
                } ${
                  integration.color === 'blue' ? 'text-blue-600 dark:text-blue-400' :
                  integration.color === 'green' ? 'text-green-600 dark:text-green-400' :
                  integration.color === 'orange' ? 'text-orange-600 dark:text-orange-400' :
                  'text-purple-600 dark:text-purple-400'
                }`}>
                  {integration.logo ? (
                    <img
                      src={integration.logo}
                      alt={integration.name}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        // Fallback to emoji if image fails to load
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                        const parent = target.parentElement
                        if (parent) {
                          parent.innerHTML = `<span class="text-3xl">${integration.fallbackIcon}</span>`
                        }
                      }}
                    />
                  ) : (
                    <span>{integration.fallbackIcon}</span>
                  )}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">{integration.name}</h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{integration.description}</p>
                {(integration as any).tool && (
                  <p className="text-xs text-primary-600 dark:text-primary-400 font-medium mb-3">
                    {(integration as any).tool}
                  </p>
                )}
                {!(integration as any).tool && (
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">&nbsp;</p>
                )}
                {integration.status === 'available' ? (
                  integration.name === 'WordPress Plugin' ? (
                    <div className="flex items-center justify-center">
                      <a
                        href={integration.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-xs font-medium inline-flex items-center gap-1"
                      >
                        Install from WordPress.org <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    </div>
                  ) : integration.name === 'Shopify App' ? (
                    <a
                      href={integration.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-xs font-medium inline-flex items-center gap-1"
                    >
                      Install on Shopify <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  ) : integration.name === 'WordPress TTS Reader' ? (
                    <div className="flex items-center justify-center gap-2">
                      <a
                        href="https://api.gistmag.co.uk/downloads/wordpress-tts-plugin"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={async (e) => {
                          e.preventDefault();
                          const downloadUrl = 'https://api.gistmag.co.uk/downloads/wordpress-tts-plugin';
                          // Open in new tab for download
                          const newWindow = window.open(downloadUrl, '_blank');
                          // Attempt to close after download starts (browsers may block this)
                          setTimeout(() => {
                            try {
                              if (newWindow && !newWindow.closed) {
                                newWindow.close();
                              }
                            } catch (err) {
                              // Browser blocked closing, which is expected
                            }
                          }, 3000);
                        }}
                        className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-xs font-medium inline-flex items-center gap-1"
                      >
                        Download <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </a>
                      <span className="text-gray-400">|</span>
                      <Link
                        to={integration.link}
                        className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-xs font-medium inline-flex items-center gap-1"
                      >
                        Learn more <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </div>
                  ) : integration.name === 'WordPress Gist Blog' ? (
                    <div className="flex items-center justify-center gap-2">
                      <a
                        href="https://api.gistmag.co.uk/downloads/wordpress-blog-meta-generator-plugin"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={async (e) => {
                          e.preventDefault();
                          const downloadUrl = 'https://api.gistmag.co.uk/downloads/wordpress-blog-meta-generator-plugin';
                          // Open in new tab for download
                          const newWindow = window.open(downloadUrl, '_blank');
                          // Attempt to close after download starts (browsers may block this)
                          setTimeout(() => {
                            try {
                              if (newWindow && !newWindow.closed) {
                                newWindow.close();
                              }
                            } catch (err) {
                              // Browser blocked closing, which is expected
                            }
                          }, 3000);
                        }}
                        className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-xs font-medium inline-flex items-center gap-1"
                      >
                        Download <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </a>
                      <span className="text-gray-400">|</span>
                      <Link
                        to={integration.link}
                        className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-xs font-medium inline-flex items-center gap-1"
                      >
                        Learn more <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </div>
                  ) : integration.link.startsWith('http') ? (
                    <a
                      href={integration.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-xs font-medium inline-flex items-center gap-1"
                    >
                      Install <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  ) : (
                    <Link
                      to={integration.link}
                      className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-xs font-medium"
                    >
                      Learn more →
                    </Link>
                  )
                ) : (
                  <span className="text-xs text-gray-500 dark:text-gray-500">Coming Soon</span>
                )}
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/dashboard/integrations"
              className="inline-flex items-center space-x-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold"
            >
              <span>View all integrations</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>

        {/* FAQ Section */}
        <div id="faq-section" className="mb-20">
          <h2 className="text-4xl font-bold text-center mb-4">Frequently Asked Questions</h2>
          <p className="text-center text-gray-600 dark:text-gray-400 mb-12">
            Everything you need to know about GistMag
          </p>
          <div className="max-w-3xl mx-auto space-y-4">
            {[
              {
                question: 'What is GistMag?',
                answer: 'GistMag is a comprehensive AI-powered platform offering multiple tools: Gist Images (alt text generation), Gist Speech (text-to-speech and speech-to-text), Gist Blog (blog meta generator), Site Audit (page health checks for performance, accessibility, best practices, and SEO), and Indexing (check whether your pages are indexed in Google). All tools are designed to help you create accessible, SEO-friendly content efficiently.'
              },
              {
                question: 'What tools does GistMag offer?',
                answer: 'GistMag offers five main tools: Gist Images for generating alt text for images, Gist Speech for text-to-speech and speech-to-text conversion, Gist Blog for generating blog post metadata including images, excerpts, categories, and tags, Site Audit for running page-level checks with scores for performance, accessibility, best practices, and SEO, and Indexing for checking whether your pages are indexed in Google (bulk-check up to 10,000 URLs). All tools are powered by advanced AI models.'
              },
              {
                question: 'How does the credit system work?',
                answer: 'Gist Images costs 1 credit per generation. Gist Speech (TTS) costs 1 credit per 1,000 characters. Gist Speech (STT) costs 5 credits per minute. Gist Blog costs 3 credits per generation. Site Audit costs 1 credit per audit. Index Check costs 1 credit per URL checked. Adding background music to TTS adds +1 credit. Credits rollover indefinitely - unused credits from your subscription or one-time purchases accumulate in your account. You can use them anytime, there\'s no expiration date.'
              },
              {
                question: 'What is Gist Blog?',
                answer: 'Gist Blog is our AI-powered blog meta generator that creates images (in different styles), excerpts, categories, and tags for your blog posts. It helps you quickly generate all the metadata you need for your blog posts, saving you time and improving your SEO.'
              },
              {
                question: 'What alt text modes are available in Gist Images?',
                answer: 'Gist Images offers three modes: Short (concise descriptions), Long (detailed descriptions), and SEO (optimized for search engines). Choose the mode that best fits your needs.'
              },
              {
                question: 'Can I use GistMag with WordPress, Shopify, or Make.com?',
                answer: 'Yes! GistMag offers integrations for WordPress (Alt Text Generator, TTS Reader, and Blog Generator plugins), Shopify (available in the Shopify App Store), Make.com, and browser extensions. You can also use our API to integrate with any platform. Check out our Integrations page for setup instructions.'
              },
              {
                question: 'Is my data secure?',
                answer: 'Absolutely. We process your images and content securely and never store them permanently. Your API key and usage data are encrypted and protected. We take privacy seriously and follow industry best practices for data security.'
              },
              {
                question: 'Do credits expire?',
                answer: 'No, credits never expire. They accumulate in your account and rollover indefinitely. When your subscription renews, new credits are added to your existing balance. One-time credit purchases also never expire.'
              },
              {
                question: 'Can I purchase credits without a subscription?',
                answer: 'Yes! We offer one-time credit packages for users who prefer pay-as-you-go. Perfect for occasional use or topping up your account. Credits purchased this way never expire.'
              },
              {
                question: 'What happens if I run out of credits?',
                answer: 'You can purchase additional credits at any time through one-time packages or upgrade your subscription plan. Your account remains active, you just need credits to use the tools.'
              },
              {
                question: 'How much do the tools cost?',
                answer: 'Gist Images: 1 credit per generation. Gist Speech (TTS): 1 credit per 1,000 characters. Gist Speech (STT): 5 credits per minute. Gist Blog: 3 credits per generation. Adding background music to TTS adds an additional 1 credit per request.'
              },
              {
                question: 'What languages are supported for Gist Speech?',
                answer: 'Gist Speech supports 9+ languages including English, Spanish, French, German, Italian, Portuguese, Japanese, Korean, and Chinese. You can select from multiple voices for each language, with options for different accents and genders.'
              },
              {
                question: 'Can I use the API for commercial projects?',
                answer: 'Yes! All plans support commercial use. Your API key can be used in production applications, WordPress sites, Shopify stores, and any other commercial projects without restrictions.'
              },
              {
                question: 'How accurate is the AI-generated content?',
                answer: 'Our AI models are trained on millions of images and text samples, providing highly accurate results. The accuracy depends on input quality and content complexity, but we continuously improve our models based on user feedback and the latest AI advancements.'
              },
              {
                question: 'Is there a rate limit on API requests?',
                answer: 'Rate limits vary by plan. Free plans have lower limits, while paid plans have higher limits and priority processing. Batch processing for TTS has a 20,000 character limit per request. Check your dashboard for your specific rate limits.'
              },
              {
                question: 'Can I cancel my subscription anytime?',
                answer: 'Yes, you can cancel your subscription at any time. Your credits will remain available until used, and you can continue using the service with your remaining credits. No cancellation fees apply.'
              },
              {
                question: 'Do you offer refunds?',
                answer: 'We offer refunds for unused subscription credits within 30 days of purchase. One-time credit purchases are non-refundable but never expire, so you can use them whenever you need them.'
              },
              {
                question: 'What image styles does Gist Blog support?',
                answer: 'Gist Blog can generate images in five different styles: Realistic, Illustration, Abstract, Minimalist, and Photographic. You can choose the style that best matches your blog post\'s theme and audience.'
              }
            ].map((faq, index) => (
              <div
                key={index}
                className="glass-card rounded-xl overflow-hidden hover-lift animate-scale-in"
                style={{ animationDelay: `${0.8 + index * 0.05}s` }}
              >
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-slate-800 transition"
                >
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white pr-4">
                    {faq.question}
                  </h3>
                  <svg
                    className={`w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0 transition-transform ${
                      openFaqIndex === index ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openFaqIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-6 pb-6">
                    <p className="text-gray-600 dark:text-gray-400">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/about"
              className="inline-flex items-center space-x-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold"
            >
              <span>Learn more about GistMag</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>

        {/* Pricing Section */}
        <div id="pricing-section" className="mb-20">
          <h2 className="text-4xl font-bold text-center mb-4">Simple, Credit-Based Pricing</h2>
          <p className="text-center text-gray-600 dark:text-gray-400 mb-4">
            Credits power every tool – from alt text and speech to blog metadata, site audits, and indexing. Choose the plan that fits your needs.
          </p>
          <div className="max-w-3xl mx-auto mb-12 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Credit Usage Details</h3>
            <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
              <li className="flex items-start space-x-2">
                <span className="text-primary-600 dark:text-primary-400 mt-0.5">•</span>
                <span><strong>Alt Text Generation:</strong> 1 credit per image</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-primary-600 dark:text-primary-400 mt-0.5">•</span>
                <span><strong>Text-to-Speech (TTS):</strong> 1 credit per 1,000 characters</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-primary-600 dark:text-primary-400 mt-0.5">•</span>
                <span><strong>Speech-to-Text (STT):</strong> 5 credits per minute of audio</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-primary-600 dark:text-primary-400 mt-0.5">•</span>
                <span><strong>Site Audit:</strong> 1 credit per audit</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-primary-600 dark:text-primary-400 mt-0.5">•</span>
                <span><strong>Index Check:</strong> 1 credit per URL checked</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-primary-600 dark:text-primary-400 mt-0.5">•</span>
                <span><strong>Background Music:</strong> +1 credit when adding music to TTS</span>
              </li>
            </ul>
          </div>
          <div className={`grid gap-4 max-w-7xl mx-auto mb-12 ${(dbPlans.length || 5) <= 5 ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}>
            {(dbPlans.length > 0 ? dbPlans : [
              { name: 'Free', credits: '50', price: '$0', period: 'month', features: ['50 credits (one-time)', 'All modes', 'API access', 'All extensions & plugins', 'Community support'] },
              { name: 'Starter', credits: '500', price: '$5', period: 'month', features: ['500 credits/month', 'All modes', 'Priority processing', 'All extensions & plugins', 'Email support'], popular: false },
              { name: 'Pro', credits: '2,000', price: '$19', period: 'month', features: ['2,000 credits/month', 'All modes', 'Priority processing', 'All extensions & plugins', 'Priority support'], popular: true },
              { name: 'Business', credits: '5,000', price: '$38', period: 'month', features: ['5,000 credits/month', 'All modes', 'Priority processing', 'All extensions & plugins', 'Priority support'], popular: false },
              { name: 'Enterprise', credits: '10,000', price: '$70', period: 'month', features: ['10,000 credits/month', 'All modes', 'Highest priority', 'All extensions & plugins', 'Dedicated support'], popular: false },
            ]).map((plan, index) => (
              <div
                key={index}
                className={`glass-card rounded-xl p-6 hover-lift animate-scale-in relative ${
                  plan.popular ? 'ring-2 ring-primary-500' : ''
                }`}
                style={{ animationDelay: `${0.7 + index * 0.1}s` }}
              >
                {plan.popular && (
                  <span className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-3 py-1 bg-primary-500 text-white text-xs font-semibold rounded-full">
                    Most Popular
                  </span>
                )}
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{plan.name}</h3>
                <div className="mb-2">
                  <div className="text-3xl font-bold text-primary-600 dark:text-primary-400">
                    {plan.price}
                    {plan.price !== '$0' && <span className="text-base text-gray-600 dark:text-gray-400">/{plan.period}</span>}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {plan.credits} credits
                  </div>
                </div>
                <ul className="space-y-2 mb-6 text-sm">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start space-x-2">
                      <svg className="w-4 h-4 text-primary-600 dark:text-primary-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={plan.name === 'Free' ? '/auth/signup' : '/dashboard/billing'}
                  className={`block w-full text-center py-2.5 rounded-lg font-semibold text-sm transition ${
                    plan.popular
                      ? 'bg-primary-500 hover:bg-primary-600 text-white'
                      : 'bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-900 dark:text-white border border-gray-300 dark:border-slate-700'
                  }`}
                >
                  {plan.name === 'Free' ? 'Get Started' : 'Subscribe'}
                </Link>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link
              to="/dashboard/billing"
              className="inline-flex items-center space-x-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold"
            >
              <span>View all plans and one-time credit packages</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 dark:border-slate-800 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-5 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img
                  src="/gistmag-rect-logo.png"
                  alt="GistMag"
                  className="h-8 w-auto"
                />
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                AI-powered tools for alt text, speech, blog metadata, site audits and indexing.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <li><Link to="/dashboard" className="hover:text-primary-600 dark:hover:text-primary-400 transition">Dashboard</Link></li>
                <li><Link to="/dashboard/api-key" className="hover:text-primary-600 dark:hover:text-primary-400 transition">API</Link></li>
                <li><Link to="/dashboard/integrations" className="hover:text-primary-600 dark:hover:text-primary-400 transition">Integrations</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Resources</h4>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <li>
                  <Link to="/about" className="hover:text-primary-600 dark:hover:text-primary-400 transition">
                    About
                  </Link>
                </li>
                <li>
                  <a
                    href="https://docs.gistmag.co.uk/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-primary-600 dark:hover:text-primary-400 transition"
                  >
                    API Docs
                  </a>
                </li>
                <li>
                  <Link
                    to="/blog"
                    className="hover:text-primary-600 dark:hover:text-primary-400 transition"
                  >
                    Blog
                  </Link>
                </li>
                <li>
                  <Link to="/dashboard/analytics" className="hover:text-primary-600 dark:hover:text-primary-400 transition">
                    Analytics
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Account</h4>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                {isAuthenticated ? (
                  <>
                    <li><Link to="/dashboard" className="hover:text-primary-600 dark:hover:text-primary-400 transition">Dashboard</Link></li>
                    <li><Link to="/dashboard/settings" className="hover:text-primary-600 dark:hover:text-primary-400 transition">Settings</Link></li>
                  </>
                ) : (
                  <>
                    <li><Link to="/auth/login" className="hover:text-primary-600 dark:hover:text-primary-400 transition">Sign In</Link></li>
                    <li><Link to="/auth/signup" className="hover:text-primary-600 dark:hover:text-primary-400 transition">Sign Up</Link></li>
                  </>
                )}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <li>
                  <Link to="/privacy" className="hover:text-primary-600 dark:hover:text-primary-400 transition">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="hover:text-primary-600 dark:hover:text-primary-400 transition">
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-200 dark:border-slate-800 flex flex-col md:flex-row justify-between items-center text-sm text-gray-600 dark:text-gray-400 gap-4">
            <p>&copy; {new Date().getFullYear()} GistMag. All rights reserved.</p>
            <div className="flex items-center space-x-1.5">
              <span>by</span>
              <a
                href="https://www.olaverse.co.uk/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-1.5 text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-semibold"
              >
                <img
                  src="https://www.olaverse.co.uk/lovable-uploads/b3a511f8-03ca-4129-b1c8-d08e38558934.png"
                  alt="Olaverse Labs"
                  className="h-4 w-4"
                />
                <span>olaverse labs</span>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
